import React, { useCallback, useMemo, useState } from 'react';

import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';

import { MonthRangePicker } from 'src/components/MonthRangePicker';
import { TerritoryFlag } from 'src/components/TerritoryFlag';

export interface FilterState {
    selectedTerritories: string[];
    selectedVendors: string[];
}

export interface CustomViewFilterProps {
    // Available options (derived from API data)
    availableTerritories?: string[];
    availableVendors?: string[];
    // Current filter state
    filterState?: FilterState;
    // Callback when filters change
    onFilterChange?: (filterState: FilterState) => void;
    // Start month in YYYY-MM format
    startMonth: string;
    // End month in YYYY-MM format
    endMonth: string;
    // Callback when user clicks Load Data with valid month range
    onMonthRangeChange: (startMonth: string, endMonth: string) => void;
}

export const CustomViewFilter: React.FC<CustomViewFilterProps> = ({
    availableTerritories = [],
    availableVendors = [],
    filterState = { selectedTerritories: [], selectedVendors: [] },
    onFilterChange,
    startMonth,
    endMonth,
    onMonthRangeChange
}) => {
    // Search query states for filtering dropdown options
    const [territorySearchQuery, setTerritorySearchQuery] = useState('');
    const [vendorSearchQuery, setVendorSearchQuery] = useState('');

    // Filter available options based on search queries
    const filteredTerritories = useMemo(() => {
        if (!territorySearchQuery) return availableTerritories;
        return availableTerritories.filter((territory) =>
            territory.toLowerCase().includes(territorySearchQuery.toLowerCase())
        );
    }, [availableTerritories, territorySearchQuery]);

    const filteredVendors = useMemo(() => {
        if (!vendorSearchQuery) return availableVendors;
        return availableVendors.filter((vendor) => vendor.toLowerCase().includes(vendorSearchQuery.toLowerCase()));
    }, [availableVendors, vendorSearchQuery]);

    // Handle territory filter change
    const handleTerritoryChange = useCallback(
        (selectedTerritories: string[]) => {
            const newFilterState = {
                ...filterState,
                selectedTerritories
            };
            onFilterChange?.(newFilterState);
        },
        [filterState, onFilterChange]
    );

    // Handle vendor filter change
    const handleVendorChange = useCallback(
        (selectedVendors: string[]) => {
            const newFilterState = {
                ...filterState,
                selectedVendors
            };
            onFilterChange?.(newFilterState);
        },
        [filterState, onFilterChange]
    );

    // Handle reset territory filter
    const handleResetTerritoryFilter = useCallback(() => {
        const newFilterState = {
            ...filterState,
            selectedTerritories: []
        };
        setTerritorySearchQuery(''); // Clear search as well
        onFilterChange?.(newFilterState);
    }, [filterState, onFilterChange]);

    // Handle reset vendor filter
    const handleResetVendorFilter = useCallback(() => {
        const newFilterState = {
            ...filterState,
            selectedVendors: []
        };
        setVendorSearchQuery(''); // Clear search as well
        onFilterChange?.(newFilterState);
    }, [filterState, onFilterChange]);

    return (
        <Box type="fill" spacingInset="400">
            <Column spacing="400">
                <Row widths={['fit', 'fit', 'fit']}>
                    <Column spacing="300">
                        <Text>Month Range</Text>
                        <MonthRangePicker
                            startMonth={startMonth}
                            endMonth={endMonth}
                            onChange={onMonthRangeChange}
                            loadButtonText="Load Data"
                        />
                    </Column>
                    <Row widths={['fill', 'fit']} alignmentVertical="bottom">
                        <Select
                            label="Territory"
                            size="large"
                            value={filterState.selectedTerritories}
                            onChange={handleTerritoryChange}
                            placeholder="Search and select territories..."
                            searchQuery={territorySearchQuery}
                            onSearch={setTerritorySearchQuery}
                        >
                            {!filteredTerritories.length ? (
                                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                                    <Text alignment="center">No results</Text>
                                </Column>
                            ) : (
                                filteredTerritories.map((territory) => (
                                    <SelectOption key={territory} value={territory} label={territory}>
                                        {() => <TerritoryFlag territoryCode={territory} />}
                                    </SelectOption>
                                ))
                            )}
                        </Select>
                        <Tooltip position={'end'} title={'Clear territory filter'}>
                            <Button
                                size="large"
                                onClick={handleResetTerritoryFilter}
                                type={'tertiary'}
                                aria-label={`clear-territory`}
                                disabled={filterState.selectedTerritories.length === 0}
                            >
                                <Icon tokens={deleteTokens} />
                            </Button>
                        </Tooltip>
                    </Row>

                    <Row widths={['fill', 'fit']} alignmentVertical="bottom">
                        <Select
                            label="Vendor"
                            size="large"
                            value={filterState.selectedVendors}
                            onChange={handleVendorChange}
                            placeholder="Search and select vendors..."
                            searchQuery={vendorSearchQuery}
                            onSearch={setVendorSearchQuery}
                        >
                            {!filteredVendors.length ? (
                                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                                    <Text alignment="center">No results</Text>
                                </Column>
                            ) : (
                                filteredVendors.map((vendor) => (
                                    <SelectOption key={vendor} value={vendor} label={vendor} />
                                ))
                            )}
                        </Select>
                        <Tooltip position={'end'} title={'Clear vendor filter'}>
                            <Button
                                size="large"
                                onClick={handleResetVendorFilter}
                                type={'tertiary'}
                                aria-label={`clear-vendor`}
                                disabled={filterState.selectedVendors.length === 0}
                            >
                                <Icon tokens={deleteTokens} />
                            </Button>
                        </Tooltip>
                    </Row>
                </Row>
                <Row>
                    <Text>Filter Results</Text>
                    <Text type="b200" truncate={true}>
                        (Territory:{' '}
                        {filterState.selectedTerritories.length === 0
                            ? 'All'
                            : filterState.selectedTerritories.join(', ')}
                        {' ;'}
                    </Text>
                    <Text type="b200" truncate={true}>
                        Vendor:{' '}
                        {filterState.selectedVendors.length === 0 ? 'All' : filterState.selectedVendors.join(', ')})
                    </Text>
                </Row>
            </Column>
        </Box>
    );
};
